import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { appAnalyticsAPI } from '../../services/api';

export const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Copied');
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); toast.success('Copied'); } catch { toast.error('Copy failed'); }
    document.body.removeChild(ta);
  }
};

export const CodeBlock: React.FC<{ code: string; title?: string; small?: boolean }> = ({ code, title, small }) => (
  <div className="ca-code mb-2">
    <div className="d-flex align-items-center justify-content-between px-3 py-1 ca-code-head">
      <span className="small text-white-50">{title || ''}</span>
      <button type="button" className="btn btn-sm btn-link text-white-50 p-0 text-decoration-none" onClick={() => copyText(code)}>
        <i className="bi bi-clipboard me-1"></i>Copy
      </button>
    </div>
    <pre className={`m-0 px-3 py-2 ${small ? 'small' : ''}`}><code>{code}</code></pre>
  </div>
);

export const fmtNum = (n: any) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
export const fmtMoney = (n: any, cur?: string | null) =>
  n === null || n === undefined ? '—' : `${cur ? cur + ' ' : ''}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
export const fmtDuration = (sec: any) => {
  if (sec === null || sec === undefined) return '—';
  const s = Number(sec);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.round((s % 86400) / 3600)}h`;
};
export const fmtDate = (d: any) => (d ? new Date(d).toLocaleString() : '—');
export const fmtAgo = (d: any) => {
  if (!d) return '—';
  const s = Math.round((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return `${Math.max(0, s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const platformIcon = (p?: string | null) =>
  p === 'ios' ? 'bi-apple' : p === 'android' ? 'bi-android2' : p === 'flutter' ? 'bi-phone' : p === 'react-native' ? 'bi-filetype-jsx' : 'bi-phone';

export const StatCard: React.FC<{ label: string; value: React.ReactNode; icon: string; color: string; hint?: string }> = ({ label, value, icon, color, hint }) => (
  <div className="card border-0 shadow-sm h-100">
    <div className="card-body py-3">
      <div className="d-flex align-items-center gap-2 mb-1">
        <span className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 30, height: 30, background: color + '1a', color }}>
          <i className={`bi ${icon}`}></i>
        </span>
        <span className="text-muted small">{label}</span>
      </div>
      <div className="fs-4 fw-bold">{value}</div>
      {hint && <div className="text-muted" style={{ fontSize: 12 }}>{hint}</div>}
    </div>
  </div>
);

export const AppNav: React.FC = () => {
  const { pathname } = useLocation();
  const items = [
    { to: '/app-analytics/overview', label: 'Overview', icon: 'bi-speedometer2' },
    { to: '/app-analytics/funnel', label: 'Lifecycle funnel', icon: 'bi-funnel' },
    { to: '/app-analytics/users', label: 'Users & journeys', icon: 'bi-people' },
    { to: '/app-analytics/setup', label: 'Tracking plan & SDK', icon: 'bi-diagram-3' },
    { to: '/docs/app-integration', label: 'Docs', icon: 'bi-journal-code' },
  ];
  return (
    <ul className="nav nav-pills small mb-3 flex-nowrap overflow-auto">
      {items.map(i => (
        <li className="nav-item" key={i.to}>
          <Link className={`nav-link py-1 ${pathname.startsWith(i.to) ? 'active' : ''}`} to={i.to}>
            <i className={`bi ${i.icon} me-1`}></i>{i.label}
          </Link>
        </li>
      ))}
    </ul>
  );
};

export interface AppFilters { from: string; to: string; platform: string; app_version: string; country: string }

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
export const defaultFilters = (days = 30): AppFilters => ({
  from: isoDay(new Date(Date.now() - (days - 1) * 86400000)), to: isoDay(new Date()), platform: '', app_version: '', country: '',
});

export const FilterBar: React.FC<{ assetId?: number; value: AppFilters; onChange: (f: AppFilters) => void; children?: React.ReactNode }> = ({ assetId, value, onChange, children }) => {
  const [opts, setOpts] = useState<{ platforms: string[]; app_versions: string[]; countries: string[] }>({ platforms: [], app_versions: [], countries: [] });
  useEffect(() => {
    if (!assetId) return;
    appAnalyticsAPI.getFilters(assetId).then(r => setOpts(r.data?.data || opts)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);
  const set = (k: keyof AppFilters, v: string) => onChange({ ...value, [k]: v });
  const preset = (days: number) => onChange({ ...value, ...{ from: defaultFilters(days).from, to: defaultFilters(days).to } });
  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body py-2 d-flex flex-wrap gap-2 align-items-center">
        <div className="btn-group btn-group-sm">
          {[7, 30, 90].map(d => (
            <button key={d} type="button" className={`btn btn-outline-secondary ${value.from === defaultFilters(d).from && value.to === defaultFilters(d).to ? 'active' : ''}`} onClick={() => preset(d)}>{d}d</button>
          ))}
        </div>
        <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={value.from} onChange={e => set('from', e.target.value)} aria-label="From" />
        <span className="text-muted small">→</span>
        <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={value.to} onChange={e => set('to', e.target.value)} aria-label="To" />
        <select className="form-select form-select-sm" style={{ width: 140 }} value={value.platform} onChange={e => set('platform', e.target.value)} aria-label="Platform">
          <option value="">All platforms</option>
          {opts.platforms.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="form-select form-select-sm" style={{ width: 140 }} value={value.app_version} onChange={e => set('app_version', e.target.value)} aria-label="App version">
          <option value="">All versions</option>
          {opts.app_versions.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="form-select form-select-sm" style={{ width: 150 }} value={value.country} onChange={e => set('country', e.target.value)} aria-label="Country">
          <option value="">All countries</option>
          {opts.countries.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        {children}
      </div>
    </div>
  );
};

export const filterParams = (assetId: number, f: AppFilters) => {
  const p: any = { asset_id: assetId, from: f.from, to: f.to };
  if (f.platform) p.platform = f.platform;
  if (f.app_version) p.app_version = f.app_version;
  if (f.country) p.country = f.country;
  return p;
};

export const NoPlanNotice: React.FC = () => (
  <div className="card border-0 shadow-sm">
    <div className="card-body text-center py-5">
      <i className="bi bi-phone text-primary" style={{ fontSize: 42 }}></i>
      <h5 className="mt-3">Connect your mobile app</h5>
      <p className="text-muted mb-3">Answer a few questions about your app's flow and we'll generate the tracking plan, SDK code and lifecycle reports.</p>
      <Link to="/app-analytics/setup" className="btn btn-primary"><i className="bi bi-magic me-1"></i>Start app setup</Link>
    </div>
  </div>
);

export const AppStyles: React.FC = () => (
  <style>{`
    .ca-code { background:#0f172a; border-radius:8px; overflow:hidden; }
    .ca-code-head { background:#1e293b; }
    .ca-code pre { color:#e2e8f0; font-size:12.5px; white-space:pre; overflow:auto; max-height:420px; }
    .ca-funnel-bar { height:34px; border-radius:6px; background:linear-gradient(90deg,#6366f1,#8b5cf6); transition:width .4s; min-width:2px; }
    .ca-funnel-track { background:#eef2ff; border-radius:6px; }
    .ca-step-row:hover { background:#f8fafc; }
    .ca-timeline { border-left:2px solid #e2e8f0; margin-left:10px; padding-left:18px; }
    .ca-tl-dot { position:absolute; left:-27px; top:4px; width:16px; height:16px; border-radius:50%; border:3px solid #fff; box-shadow:0 0 0 1px #cbd5e1; }
    .ca-milestone { background:#f5f3ff; border:1px solid #ddd6fe; }
    .ca-conversion { background:#ecfdf5; border:1px solid #a7f3d0; }
    .ca-stage-pill { font-size:11px; }
  `}</style>
);
